/**
 * Tutar okuma.
 *
 * Tek gerçek belirsizlik şudur: TEK ayraç ve arkasında TAM 3 hane.
 *
 *   "1,234"  →  1234    (ayraç binlik, ABD)   ya da   1.234 (ayraç ondalık, AB)
 *   "1.234"  →  1234    (ayraç binlik, AB)    ya da   1.234 (ayraç ondalık, ABD)
 *
 * Aradaki fark 1000 KAT. Eski kod bunu koşulsuz Avrupa okuyup 1234 yazıyordu;
 * ABD'li bir kullanıcının 1.234'lük birim fiyatı defterine 1234 olarak
 * geçiyordu. Artık iki okuma da hesaplanır, farklıysa değer onaya çıkar.
 *
 * Diğer bütün biçimler KANITLA çözülür, yerel ayara hiç bakılmadan:
 *   - iki ayraç türü de varsa   → sondaki ondalıktır          ("1.234,56")
 *   - aynı ayraç birden çoksa   → binliktir                   ("1.234.567")
 *   - arkasında 1-2 hane varsa  → ondalıktır                  ("12,50")
 *   - arkasında 4+ hane varsa   → ondalıktır                  ("1,2345")
 * Binlik ayracı her zaman tam 3 hane gruplar; bu kural belirsizliği kaldırır.
 */

import type { Locale } from './locale.js';
import { type Parsed, sure, unsure, none } from './parsed.js';

/** Muhasebede parantez eksi demektir: "(1.234,56)" = -1234.56 */
const PARENS = /^\((.*)\)$/;
/** Bazı banka ekstrelerinde eksi sonda gelir: "1234,56-" */
const TRAILING_MINUS = /^(.*)-$/;

function stripSign(raw: string): { body: string; negative: boolean } {
  let s = raw.trim();
  let negative = false;
  const p = s.match(PARENS);
  if (p) { s = p[1].trim(); negative = true; }
  if (s.startsWith('-')) { s = s.slice(1).trim(); negative = true; }
  else if (s.startsWith('+')) { s = s.slice(1).trim(); }
  const t = s.match(TRAILING_MINUS);
  if (t) { s = t[1].trim(); negative = true; }
  return { body: s, negative };
}

/** Rakam ve ayraç dışındaki her şeyi at: para simgesi, kod, boşluk, NBSP. */
function keepNumeric(s: string): string {
  return s.replace(/[  \s]/g, ' ').replace(/[^\d.,' ]/g, '').trim();
}

function digitsOnly(s: string): string { return s.replace(/[^\d]/g, ''); }

function build(intPart: string, fracPart: string, negative: boolean): number | null {
  const i = digitsOnly(intPart);
  const f = digitsOnly(fracPart);
  if (!i && !f) return null;
  const n = parseFloat((i || '0') + (f ? '.' + f : ''));
  if (!isFinite(n)) return null;
  return negative ? -n : n;
}

/**
 * Metinden tutar oku.
 *
 * @param raw     Ham hücre/metin. Para simgesi, boşluk, parantez içerebilir.
 * @param locale  Yalnızca gerçek belirsizlikte başvurulur.
 */
export function parseAmount(raw: unknown, locale: Locale): Parsed<number> {
  if (raw === null || raw === undefined) return none('empty');
  if (typeof raw === 'number') {
    return isFinite(raw) ? sure(raw) : none('not a number');
  }

  const { body, negative } = stripSign(String(raw));
  const cleaned = keepNumeric(body);
  if (!cleaned || !/\d/.test(cleaned)) return none('no number found');

  /* Boşluk ve kesme işareti yalnızca binlik ayracı olabilir (fr-FR, de-CH). */
  const s = cleaned.replace(/[ ']/g, '');
  const dots = (s.match(/\./g) || []).length;
  const commas = (s.match(/,/g) || []).length;

  /* Ayraç yok → düz tam sayı. Kanıtlı. */
  if (!dots && !commas) {
    const n = build(s, '', negative);
    return n === null ? none('no number found') : sure(n);
  }

  /* Her iki ayraç da var → SONDAKİ ondalıktır. Kanıtlı. */
  if (dots && commas) {
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    const cut = Math.max(lastDot, lastComma);
    const n = build(s.slice(0, cut), s.slice(cut + 1), negative);
    return n === null ? none('no number found') : sure(n);
  }

  /* Tek tür ayraç var. */
  const sep = dots ? '.' : ',';
  const count = dots || commas;
  const tail = s.slice(s.lastIndexOf(sep) + 1);

  /* Birden çok kez geçiyorsa binliktir; ondalık ayraç bir kez geçebilir. */
  if (count > 1) {
    const n = build(s.replace(/[.,]/g, ''), '', negative);
    return n === null ? none('no number found') : sure(n);
  }

  /* Arkasında 3 haneden farklı sayıda hane varsa binlik OLAMAZ → ondalıktır. */
  if (tail.length !== 3) {
    const cut = s.lastIndexOf(sep);
    const n = build(s.slice(0, cut), tail, negative);
    return n === null ? none('no number found') : sure(n);
  }

  /* ── Tek gerçek belirsizlik: tek ayraç + tam 3 hane ────────────────── */
  const asGroup = build(s.replace(/[.,]/g, ''), '', negative);      // 1,234 → 1234
  const asDecimal = build(s.slice(0, s.lastIndexOf(sep)), tail, negative); // → 1.234
  if (asGroup === null || asDecimal === null) return none('no number found');

  /* Baştaki sıfır ondalığı kanıtlar: "0,123" binlik olamaz. */
  if (/^0+$/.test(s.slice(0, s.lastIndexOf(sep)))) return sure(asDecimal);

  const localeSaysDecimal = locale.decimal === sep;
  const chosen = localeSaysDecimal ? asDecimal : asGroup;
  const other = localeSaysDecimal ? asGroup : asDecimal;

  return unsure(
    chosen,
    'Ambiguous separator: "' + sep + '" could be either a thousands or a decimal separator in this document.',
    other,
  );
}
